import { StandardEvents } from '@shopify/events';

/** How long the "Added" confirmation stays before the button returns to rest. */
const ADDED_DURATION = 1600;

/**
 * Busy and confirmation states for an add-to-cart button: product cards, the "complete the pair"
 * cards and drawer, the buy box button and the sticky bar.
 *
 * Horizon's `<product-form-component>` does the actual add but gives no feedback of its own on
 * these buttons. Its `CartLinesUpdateEvent` fires the moment the request goes out, and the event's
 * promise settles when the cart has been re-read, so the button spins for exactly as long as the
 * add takes.
 *
 * Where the element wraps the form, the event bubbles up to it. Where the button sits outside the
 * form (buy box button, sticky bar), set `data-listen="document"` and `data-product-id`: it then
 * listens on the document and reacts only to adds of that product.
 *
 * While busy, further clicks and submits are swallowed, so a double tap adds one bottle, not two.
 * The button stays focusable (no `disabled`) so keyboard focus isn't dropped mid-add;
 * product-form-component announces "Added" through its own live region.
 *
 * A plain element, not a `Component`: declarative `on:click="/method"` handlers resolve to the
 * closest Component, and this wrapper must not capture the sticky bar's or the buy box's.
 * The button is found by selector, since Component refs stop at a nested component anyway.
 */
class OudhCardAtc extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit, { capture: true });
    this.addEventListener('click', this.#onSubmit, { capture: true });
    this.#eventTarget.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit, { capture: true });
    this.removeEventListener('click', this.#onSubmit, { capture: true });
    this.#eventTarget.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    clearTimeout(this.#timer);
  }

  /** @returns {EventTarget} */
  get #eventTarget() {
    return this.dataset.listen === 'document' ? document : this;
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    if (this.dataset.state !== 'busy') return;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  /** @param {Event} event */
  #onCartLinesUpdate = (event) => {
    const { promise } = /** @type {import('@shopify/events').CartLinesUpdateEvent} */ (event);

    if (this.dataset.listen === 'document') {
      const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      if (!form || !this.dataset.productId || form.dataset.productId !== this.dataset.productId) return;
    }

    this.#setState('busy');

    if (!promise) {
      this.#setState();
      return;
    }

    promise
      .then(({ detail }) => {
        if (detail?.didError) {
          this.#setState();
          return;
        }
        this.#setState('added');
        this.#timer = setTimeout(() => this.#setState(), ADDED_DURATION);
      })
      .catch(() => this.#setState());
  };

  /** @param {'busy' | 'added'} [state] */
  #setState(state) {
    clearTimeout(this.#timer);
    const button = this.querySelector('button[type="submit"], button[data-atc]');

    if (state) this.dataset.state = state;
    else delete this.dataset.state;

    if (!button) return;
    if (state === 'busy') button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }
}

if (!customElements.get('oudh-card-atc')) {
  customElements.define('oudh-card-atc', OudhCardAtc);
}
